import { Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { IconPersonSmall } from '@amzn/stencil-react-icons';
import { formatAssigneeLabel } from '../domain';

interface AssigneesDisplayProps {
  /**
   * List of assignee aliases. Renders nothing when empty. Accepts
   * `readonly` arrays so callers can safely pass module-level
   * frozen constants (e.g. an EMPTY_ASSIGNEES sentinel used to keep
   * the useMemo output reference-stable).
   */
  assignees: readonly string[] | null | undefined;
  /** Stencil color token for the text and icon. Defaults to secondary. */
  color?: string;
}

/**
 * Compact assignee display for cards and list items.
 *
 * - 0 assignees → renders nothing
 * - 1 assignee  → 👤 alice
 * - N assignees → 👤 alice (+N-1)
 *
 * Use in any feature card that shows assignee attribution.
 * Formatting logic lives in shared/domain/assigneeUtils.ts.
 */
export function AssigneesDisplay({
  assignees,
  color = 'color.text.secondary',
}: AssigneesDisplayProps): JSX.Element | null {
  const label = formatAssigneeLabel(assignees);
  if (!label) return null;

  return (
    <Row alignItems="center" gridGap="dimensions.spacing.100">
      <IconPersonSmall color={color} aria-hidden />
      <Text color={color}>{label}</Text>
    </Row>
  );
}
